import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Calendar,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleDashed,
  Clock,
  ExternalLink,
  Flame,
  MessageCircleOff,
  Pencil,
  Plane,
  Rocket,
  Shield,
  Train,
  TrendingDown,
  Users,
  UserX,
  X,
} from 'lucide-react';
import {
  EVENT_TYPE_LABEL,
  EVENT_TYPE_SHORT,
  api,
  type AttendanceStatus,
  type DashboardStatPlayer,
  type DashboardStats,
  type EventSummary,
  type EventType,
  type Participation,
  type SlotKey,
  type SquadLeader,
  type TrainAssignment,
} from '@/lib/api';
import { cn, formatPower } from '@/lib/utils';
import { PlayerLabel } from '@/components/PlayerLabel';
import SyncButton from '@/components/SyncButton';
import ServerClockChip from '@/components/ServerClockChip';
import VsWeekChip from '@/components/VsWeekChip';

function startOfWeek(d: Date): Date {
  const out = new Date(d);
  const day = out.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  out.setDate(out.getDate() + diff);
  out.setHours(0, 0, 0, 0);
  return out;
}

function iso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function fmtRange(start: Date, end: Date): string {
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  return `${start.toLocaleDateString(undefined, opts)} – ${end.toLocaleDateString(undefined, opts)}`;
}

export default function WeekDashboard() {
  const [offset, setOffset] = useState(0); // weeks from current
  const baseStart = useMemo(() => {
    const s = startOfWeek(new Date());
    s.setDate(s.getDate() + offset * 7);
    return s;
  }, [offset]);
  const baseEnd = useMemo(() => {
    const e = new Date(baseStart);
    e.setDate(e.getDate() + 6);
    return e;
  }, [baseStart]);

  const weekEvents = useQuery({
    queryKey: ['events-week', iso(baseStart), iso(baseEnd)],
    queryFn: () => api.listEvents({ from: iso(baseStart), to: iso(baseEnd) }),
  });

  const events = weekEvents.data ?? [];
  const cs = events.find((e) => e.type === 'canyon_storm');
  const ds = events.find((e) => e.type === 'desert_storm');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">Weekly dashboard</h1>
        <div className="flex items-center gap-1 rounded-lg border border-slate-800 bg-slate-900 p-0.5 text-sm">
          <button
            onClick={() => setOffset((o) => o - 1)}
            className="rounded px-2 py-1 text-slate-400 hover:bg-slate-800 hover:text-white"
            title="Previous week"
          >
            <ChevronLeft size={14} />
          </button>
          <span className="px-3 py-1 tabular-nums text-slate-300">
            {fmtRange(baseStart, baseEnd)}
            {offset !== 0 && (
              <button
                onClick={() => setOffset(0)}
                className="ml-2 text-xs text-indigo-400 hover:underline"
              >
                today
              </button>
            )}
          </span>
          <button
            onClick={() => setOffset((o) => o + 1)}
            className="rounded px-2 py-1 text-slate-400 hover:bg-slate-800 hover:text-white"
            title="Next week"
          >
            <ChevronRight size={14} />
          </button>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <ServerClockChip />
          <SyncButton />
          <Link
            to="/events"
            className="rounded border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
          >
            All events
          </Link>
        </div>
      </div>

      <VsWeekChip />

      <StatsTiles />

      {weekEvents.isLoading && <div className="text-slate-500">Loading…</div>}
      {!weekEvents.isLoading && events.length === 0 && (
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-12 text-center text-slate-500">
          No events scheduled this week.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <EventColumn type="canyon_storm" event={cs} />
        <EventColumn type="desert_storm" event={ds} />
      </div>
    </div>
  );
}

function StatsTiles() {
  const statsQuery = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: api.dashboardStats,
  });
  const stats = statsQuery.data;
  return (
    <div className="space-y-3">
      <UpcomingTrainsTile />
      <PendingSelfReportsTile />
      <ActiveChatBansTile />
      <ActiveFlagsTile />
      <OnLeaveTile />
      <div className="grid gap-3 md:grid-cols-3">
        <StatTile
          title="Should be picked"
          subtitle="Most overdue applicants"
          icon={<Flame size={16} className="text-orange-400" />}
          accent="orange"
          players={stats?.shouldBePicked}
          metricKey="consecutiveNotPicked"
          emptyText="Everyone's been picked recently."
        />
        <StatTile
          title="Recent no-shows"
          subtitle="Selected but didn't attend"
          icon={<UserX size={16} className="text-red-400" />}
          accent="red"
          players={stats?.recentNoShows}
          metricKey="consecutiveNoShows"
          emptyText="No recent no-shows."
        />
        <StatTile
          title="Long-running subs"
          subtitle="Sub-picked, never started"
          icon={<TrendingDown size={16} className="text-amber-400" />}
          accent="amber"
          players={stats?.longRunningSubs}
          metricKey="consecutiveSubsOnly"
          emptyText="No one stuck on the bench."
        />
      </div>
      <div className="grid gap-3 md:grid-cols-4">
        <ProfessionTile stats={stats} />
        <SquadTile
          title="Air Squad"
          icon={<Plane size={16} className="text-sky-400" />}
          accent="sky"
          leaders={stats?.strongestByM1Type.planes}
        />
        <SquadTile
          title="Rocket Squad"
          icon={<Rocket size={16} className="text-rose-400" />}
          accent="rose"
          leaders={stats?.strongestByM1Type.rockets}
        />
        <SquadTile
          title="Tank Squad"
          icon={<Shield size={16} className="text-emerald-400" />}
          accent="emerald"
          leaders={stats?.strongestByM1Type.tanks}
        />
      </div>
      <RecentRenamesTile renames={stats?.recentRenames} />
    </div>
  );
}

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function isoLocalDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

function UpcomingTrainsTile() {
  const start = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const days = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const d = new Date(start);
        d.setDate(d.getDate() + i);
        return d;
      }),
    [start],
  );
  const from = isoLocalDate(days[0]);
  const to = isoLocalDate(days[days.length - 1]);

  const trainsQuery = useQuery({
    queryKey: ['trains', from, to],
    queryFn: () => api.listTrains({ from, to }),
  });

  const byDate = useMemo(() => {
    const map = new Map<string, TrainAssignment>();
    for (const t of trainsQuery.data ?? []) map.set(t.date, t);
    return map;
  }, [trainsQuery.data]);

  const todayISO = isoLocalDate(new Date());

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
      <header className="mb-2 flex items-center gap-2">
        <Train size={16} className="text-indigo-300" />
        <div>
          <div className="text-sm font-semibold">Upcoming trains</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            Next 7 days
          </div>
        </div>
        <Link
          to="/trains"
          className="ml-auto text-xs text-slate-400 hover:text-white"
        >
          Manage →
        </Link>
      </header>
      {trainsQuery.isLoading ? (
        <div className="text-xs text-slate-500">Loading…</div>
      ) : (
        <ul className="grid grid-cols-2 gap-1.5 sm:grid-cols-4 lg:grid-cols-7">
          {days.map((d) => {
            const iso = isoLocalDate(d);
            const assignment = byDate.get(iso) ?? null;
            const isToday = iso === todayISO;
            const label =
              assignment &&
              (assignment.player.displayName?.trim() || assignment.player.name);
            return (
              <li
                key={iso}
                className={cn(
                  'rounded border px-2 py-1.5 text-xs',
                  isToday
                    ? 'border-indigo-600/70 bg-indigo-900/20'
                    : 'border-slate-800 bg-slate-950/40',
                )}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-medium text-slate-200">
                    {WEEKDAY_SHORT[d.getDay()]}
                  </span>
                  <span className="tabular-nums text-[10px] text-slate-500">
                    {iso.slice(5)}
                  </span>
                </div>
                <div className="mt-0.5 truncate" title={label ?? undefined}>
                  {label ? (
                    <span className="text-slate-100">{label}</span>
                  ) : (
                    <span className="text-slate-600">Unassigned</span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function RecentRenamesTile({
  renames,
}: {
  renames: DashboardStats['recentRenames'] | undefined;
}) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
      <header className="mb-2 flex items-center gap-2">
        <Pencil size={16} className="text-indigo-300" />
        <div>
          <div className="text-sm font-semibold">Recent renames</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            In-game name changes — last 7 days
          </div>
        </div>
      </header>
      {!renames ? (
        <div className="text-xs text-slate-500">Loading…</div>
      ) : renames.length === 0 ? (
        <div className="text-xs text-slate-500">No renames in the last 7 days.</div>
      ) : (
        <ul className="grid gap-1 text-xs md:grid-cols-2">
          {renames.map((r) => (
            <li
              key={r.id}
              className="flex items-center gap-2 rounded bg-slate-950/40 px-2 py-1"
            >
              <span className="w-16 shrink-0 tabular-nums text-slate-500">
                {r.changedAt.slice(5, 10)}
              </span>
              <span className="truncate text-slate-500" title={r.oldName}>
                {r.oldName}
              </span>
              <span className="text-slate-600">→</span>
              <span className="truncate" title={r.newName}>
                {r.newName}
              </span>
              <span className="ml-auto text-[10px] uppercase text-slate-600">
                {r.source}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ProfessionTile({ stats }: { stats?: DashboardStats }) {
  const counts = stats?.professionCounts;
  const total = counts ? counts.warlord + counts.engineer + counts.none : 0;
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
      <header className="mb-2 flex items-center gap-2">
        <Users size={16} className="text-indigo-400" />
        <div>
          <div className="text-sm font-semibold">Professions</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            {total} active players
          </div>
        </div>
      </header>
      {!counts ? (
        <div className="text-xs text-slate-500">Loading…</div>
      ) : (
        <ul className="space-y-1 text-xs">
          <ProfRow label="Warlord" value={counts.warlord} accent="text-rose-300" />
          <ProfRow label="Engineer" value={counts.engineer} accent="text-sky-300" />
          <ProfRow label="None set" value={counts.none} accent="text-slate-500" />
        </ul>
      )}
    </div>
  );
}

function ProfRow({ label, value, accent }: { label: string; value: number; accent: string }) {
  return (
    <li className="flex items-center justify-between">
      <span className={accent}>{label}</span>
      <span className="rounded bg-slate-800/80 px-1.5 py-0.5 tabular-nums font-semibold text-slate-100">
        {value}
      </span>
    </li>
  );
}

const SQUAD_BORDER: Record<string, string> = {
  sky: 'border-sky-900/50',
  rose: 'border-rose-900/50',
  emerald: 'border-emerald-900/50',
};

function SquadTile({
  title,
  icon,
  accent,
  leaders,
}: {
  title: string;
  icon: React.ReactNode;
  accent: 'sky' | 'rose' | 'emerald';
  leaders: SquadLeader[] | undefined;
}) {
  return (
    <div className={cn('rounded-lg border bg-slate-900/40 p-3', SQUAD_BORDER[accent])}>
      <header className="mb-2 flex items-center gap-2">
        {icon}
        <div>
          <div className="text-sm font-semibold">{title}</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            Top 5 with this M1
          </div>
        </div>
      </header>
      {leaders === undefined ? (
        <div className="text-xs text-slate-500">Loading…</div>
      ) : leaders.length === 0 ? (
        <div className="text-xs text-slate-500">No data yet.</div>
      ) : (
        <ul className="space-y-1">
          {leaders.map((l, i) => (
            <li key={l.id} className="flex items-center gap-1.5 text-xs">
              <span className="w-4 tabular-nums text-slate-500">{i + 1}</span>
              <span className="flex-1 truncate" title={l.name}>
                {l.name}
              </span>
              <span className="tabular-nums text-slate-200" title="Squad strength (M1 power)">
                {formatPower(l.m1Power)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const ACCENT_BORDER: Record<string, string> = {
  orange: 'border-orange-900/50',
  red: 'border-red-900/50',
  amber: 'border-amber-900/50',
};
const ACCENT_BADGE: Record<string, string> = {
  orange: 'bg-orange-900/40 text-orange-300',
  red: 'bg-red-900/40 text-red-300',
  amber: 'bg-amber-900/40 text-amber-300',
};

function PendingSelfReportsTile() {
  const q = useQuery({
    queryKey: ['self-reports', 'pending'],
    queryFn: api.listPendingSelfReports,
    refetchInterval: 60_000,
  });
  const count = q.data?.rows.length ?? 0;
  if (q.isLoading || count === 0) return null;
  return (
    <Link
      to="/roster/self-reports"
      className="flex items-center gap-3 rounded-lg border border-indigo-800/60 bg-indigo-900/20 p-3 hover:bg-indigo-900/30"
    >
      <Users size={18} className="text-indigo-300" />
      <div className="flex-1">
        <div className="text-sm font-semibold text-indigo-100">
          {count} self-report{count === 1 ? '' : 's'} waiting for review
        </div>
        <div className="text-[10px] uppercase tracking-wider text-indigo-300/70">
          Approve or reject
        </div>
      </div>
      <ChevronRight size={16} className="text-indigo-300" />
    </Link>
  );
}

function ActiveChatBansTile() {
  const q = useQuery({
    queryKey: ['admin-chat-bans'],
    queryFn: api.listAdminChatBans,
    refetchInterval: 60_000,
  });
  const now = Date.now();
  const active = (q.data?.bans ?? []).filter(
    (b) => b.active && Date.parse(b.endsAtUtc) > now,
  );
  if (q.isLoading || active.length === 0) return null;
  return (
    <div className="rounded-lg border border-rose-900/60 bg-rose-950/20 p-3">
      <div className="mb-2 flex items-center gap-2">
        <MessageCircleOff size={16} className="text-rose-300" />
        <div className="text-sm font-semibold text-rose-100">
          {active.length} chat ban{active.length === 1 ? '' : 's'} active
        </div>
      </div>
      <div className="space-y-1">
        {active
          .slice()
          .sort((a, b) => (a.endsAtUtc < b.endsAtUtc ? -1 : 1))
          .map((b) => (
            <ActiveChatBanRow key={b.id} ban={b} />
          ))}
      </div>
    </div>
  );
}

function ActiveChatBanRow({
  ban,
}: {
  ban: import('@/lib/api').AdminChatBan;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const delta = Date.parse(ban.endsAtUtc) - now;
  const label = ban.player.displayName?.trim() || ban.player.name;
  return (
    <div className="flex items-baseline justify-between gap-2 text-xs">
      <span className="truncate text-rose-100">
        <span className="font-medium">{label}</span>{' '}
        <span className="text-rose-300/70">· {ban.durationHours}h</span>
      </span>
      <span className="whitespace-nowrap tabular-nums text-rose-200">
        {formatChatBanCountdown(delta)}
      </span>
    </div>
  );
}

function formatChatBanCountdown(ms: number): string {
  if (ms <= 0) return 'ended';
  const total = Math.floor(ms / 1000);
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
  if (d > 0) return `${d}d ${pad(h)}:${pad(m)}:${pad(s)}`;
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

function ActiveFlagsTile() {
  const playersQuery = useQuery({
    queryKey: ['players'],
    queryFn: () => api.listPlayers(),
  });
  const bucketed = useMemo(() => {
    const red: { name: string; note: string | null; expiresAt: string | null }[] = [];
    const yellow: { name: string; count: number; notes: string[]; expiresAt: string | null }[] = [];
    const green: { name: string; note: string | null; expiresAt: string | null }[] = [];
    const byName = (a: { name: string }, b: { name: string }) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    for (const p of playersQuery.data ?? []) {
      const flags = p.activeFlags ?? [];
      const label = (p.displayName?.trim() || p.name).trim();
      const rFlag = flags.find((f) => f.kind === 'red');
      const yFlags = flags.filter((f) => f.kind === 'yellow');
      const gFlag = flags.find((f) => f.kind === 'green');
      if (rFlag || yFlags.length >= 2) {
        red.push({
          name: label,
          note: rFlag?.note ?? (yFlags.length >= 2 ? '2 yellow cards' : null),
          expiresAt: rFlag?.expiresAt ?? null,
        });
      } else if (yFlags.length === 1) {
        yellow.push({
          name: label,
          count: 1,
          notes: yFlags.map((f) => f.note ?? '').filter(Boolean),
          expiresAt: yFlags[0].expiresAt,
        });
      }
      if (gFlag) {
        green.push({ name: label, note: gFlag.note, expiresAt: gFlag.expiresAt });
      }
    }
    red.sort(byName);
    yellow.sort(byName);
    green.sort(byName);
    return { red, yellow, green };
  }, [playersQuery.data]);

  const fmt = (iso: string | null) => {
    if (!iso) return null;
    const days = Math.round(
      (new Date(iso).getTime() - Date.now()) / (24 * 60 * 60 * 1000),
    );
    if (days < 0) return 'expired';
    if (days <= 0) return 'today';
    if (days === 1) return '1d';
    return `${days}d`;
  };

  return (
    <div className="grid gap-3 md:grid-cols-3">
      <FlagColumn
        title="Red cards"
        accent="border-red-900/60 bg-red-900/10"
        titleClass="text-red-200"
        dotClass="bg-red-500"
        empty="No active red cards."
        items={bucketed.red.map((r) => ({
          name: r.name,
          note: r.note,
          meta: fmt(r.expiresAt),
        }))}
      />
      <FlagColumn
        title="Yellow cards"
        accent="border-yellow-900/60 bg-yellow-900/10"
        titleClass="text-yellow-200"
        dotClass="bg-yellow-400"
        empty="No active yellow cards."
        items={bucketed.yellow.map((y) => ({
          name: y.name,
          note: y.notes.join(' · ') || null,
          meta: fmt(y.expiresAt),
        }))}
      />
      <FlagColumn
        title="Should be picked"
        accent="border-emerald-900/60 bg-emerald-900/10"
        titleClass="text-emerald-200"
        dotClass="bg-emerald-500"
        empty="No active green flags."
        items={bucketed.green.map((g) => ({
          name: g.name,
          note: g.note,
          meta: fmt(g.expiresAt),
        }))}
      />
    </div>
  );
}

function OnLeaveTile() {
  const playersQuery = useQuery({
    queryKey: ['players'],
    queryFn: () => api.listPlayers(),
  });
  const onLeave = useMemo(() => {
    const rows = (playersQuery.data ?? [])
      .filter((p) => p.currentLeave)
      .map((p) => ({
        id: p.id,
        name: (p.displayName?.trim() || p.name).trim(),
        startsAt: p.currentLeave!.startsAt,
        endsAt: p.currentLeave!.endsAt,
        reason: p.currentLeave!.reason,
      }));
    rows.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    return rows;
  }, [playersQuery.data]);

  const fmtReturn = (endsAt: string | null): string | null => {
    if (!endsAt) return null;
    const days = Math.round(
      (new Date(endsAt).getTime() - Date.now()) / (24 * 60 * 60 * 1000),
    );
    if (days < 0) return 'ended';
    if (days === 0) return 'back today';
    if (days === 1) return 'back tomorrow';
    return `back in ${days}d`;
  };

  return (
    <div className="rounded-lg border border-amber-900/60 bg-amber-900/10 p-3">
      <header className="mb-2 flex items-center gap-2">
        <span className="text-base leading-none">🌴</span>
        <div>
          <div className="text-sm font-semibold text-amber-100">On leave</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            Currently away
          </div>
        </div>
        <span className="ml-auto text-xs text-slate-500">{onLeave.length}</span>
      </header>
      {playersQuery.isLoading ? (
        <div className="text-xs text-slate-500">Loading…</div>
      ) : onLeave.length === 0 ? (
        <div className="text-xs text-slate-500">Nobody on leave.</div>
      ) : (
        <ul className="grid gap-1 text-xs md:grid-cols-2 lg:grid-cols-3">
          {onLeave.map((p) => {
            const ret = fmtReturn(p.endsAt);
            return (
              <li
                key={p.id}
                className="rounded border border-slate-800/70 bg-slate-950/40 px-2 py-1"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-medium text-slate-200">{p.name}</span>
                  {ret && <span className="text-[10px] text-slate-500">{ret}</span>}
                </div>
                <div className="mt-0.5 text-[11px] text-slate-500">
                  since {p.startsAt}
                  {p.endsAt ? ` · until ${p.endsAt}` : ' · open-ended'}
                </div>
                {p.reason && (
                  <div className="mt-0.5 whitespace-pre-wrap text-[11px] text-slate-400">
                    {p.reason}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function FlagColumn({
  title,
  accent,
  titleClass,
  dotClass,
  empty,
  items,
}: {
  title: string;
  accent: string;
  titleClass: string;
  dotClass: string;
  empty: string;
  items: { name: string; note: string | null; meta: string | null }[];
}) {
  return (
    <div className={cn('rounded-lg border p-3', accent)}>
      <header className={cn('mb-2 flex items-center gap-2 text-sm font-semibold', titleClass)}>
        <span className={cn('inline-block h-2.5 w-2.5 rounded-full', dotClass)} />
        {title}
        <span className="ml-auto text-xs text-slate-500">{items.length}</span>
      </header>
      {items.length === 0 ? (
        <div className="text-xs text-slate-500">{empty}</div>
      ) : (
        <ul className="space-y-1">
          {items.map((item, i) => (
            <li
              key={`${item.name}-${i}`}
              className="rounded border border-slate-800/70 bg-slate-950/40 px-2 py-1 text-xs"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate font-medium text-slate-200">{item.name}</span>
                {item.meta && <span className="text-[10px] text-slate-500">{item.meta}</span>}
              </div>
              {item.note && (
                <div className="mt-0.5 whitespace-pre-wrap text-[11px] text-slate-400">
                  {item.note}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatTile({
  title,
  subtitle,
  icon,
  accent,
  players,
  metricKey,
  emptyText,
}: {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  accent: 'orange' | 'red' | 'amber';
  players: DashboardStatPlayer[] | undefined;
  metricKey: 'consecutiveNotPicked' | 'consecutiveNoShows' | 'consecutiveSubsOnly';
  emptyText: string;
}) {
  return (
    <div className={cn('rounded-lg border bg-slate-900/40 p-3', ACCENT_BORDER[accent])}>
      <header className="mb-2 flex items-center gap-2">
        {icon}
        <div>
          <div className="text-sm font-semibold">{title}</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">{subtitle}</div>
        </div>
      </header>
      {!players ? (
        <div className="text-xs text-slate-500">Loading…</div>
      ) : players.length === 0 ? (
        <div className="text-xs text-slate-500">{emptyText}</div>
      ) : (
        <ul className="space-y-1">
          {players.slice(0, 5).map((p) => (
            <li key={p.id} className="flex items-center gap-2 text-xs">
              <span className="flex-1 truncate" title={p.name}>
                {p.name}
              </span>
              <span className="text-slate-500">{p.rank}</span>
              <span
                className={cn(
                  'rounded px-1.5 py-0.5 tabular-nums font-semibold',
                  ACCENT_BADGE[accent],
                )}
              >
                {p.metrics[metricKey]}×
              </span>
            </li>
          ))}
          {players.length > 5 && (
            <li className="text-[10px] text-slate-500">+{players.length - 5} more…</li>
          )}
        </ul>
      )}
    </div>
  );
}

function EventColumn({ type, event }: { type: EventType; event?: EventSummary }) {
  const partQuery = useQuery({
    queryKey: ['participations', event?.id ?? -1],
    queryFn: () => api.listParticipations(event!.id),
    enabled: !!event && event.status !== 'skipped',
  });

  if (!event) {
    return (
      <div className="rounded-lg border border-dashed border-slate-800 bg-slate-900/20 p-5 text-sm text-slate-500">
        <div className="mb-1 text-xs uppercase tracking-wider">
          {EVENT_TYPE_LABEL[type]}
        </div>
        <div>No {EVENT_TYPE_SHORT[type]} scheduled this week.</div>
      </div>
    );
  }

  if (event.status === 'skipped') {
    return (
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-5 text-sm">
        <Header event={event} />
        <div className="mt-3 text-slate-400">This event is skipped.</div>
      </div>
    );
  }

  const participations = partQuery.data ?? [];
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <Header event={event} />
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        {(['A', 'B'] as SlotKey[]).map((slot) => (
          <SlotColumn
            key={slot}
            event={event}
            slot={slot}
            participations={participations}
            loading={partQuery.isLoading}
          />
        ))}
      </div>
    </div>
  );
}

function Header({ event }: { event: EventSummary }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Calendar size={16} className="text-slate-500" />
      <h2 className="font-semibold">{EVENT_TYPE_LABEL[event.type]}</h2>
      <span className="text-sm text-slate-400">{event.eventDate}</span>
      <StatusChip status={event.status} />
      {event.counts.pendingAttendance > 0 && (
        <span className="rounded bg-amber-700/50 px-2 py-0.5 text-xs text-amber-100">
          {event.counts.pendingAttendance} attendance pending
        </span>
      )}
      <Link
        to={`/events/${event.id}`}
        className="ml-auto inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
      >
        Open <ExternalLink size={11} />
      </Link>
    </div>
  );
}

function StatusChip({ status }: { status: EventSummary['status'] }) {
  const cls =
    status === 'planned'
      ? 'bg-indigo-700/50 text-indigo-100'
      : status === 'completed'
        ? 'bg-emerald-700/50 text-emerald-100'
        : 'bg-slate-700 text-slate-300';
  return <span className={cn('rounded px-2 py-0.5 text-xs capitalize', cls)}>{status}</span>;
}

function SlotColumn({
  event,
  slot,
  participations,
  loading,
}: {
  event: EventSummary;
  slot: SlotKey;
  participations: Participation[];
  loading: boolean;
}) {
  const slotInfo = event.slots.find((s) => s.slotKey === slot);
  const here = participations.filter((p) => p.assignedSlot === slot);
  const starters = here
    .filter((p) => p.assignedRole === 'starter')
    .sort((a, b) => (a.assignedPosition ?? 0) - (b.assignedPosition ?? 0));
  const subs = here
    .filter((p) => p.assignedRole === 'sub')
    .sort((a, b) => (a.assignedPosition ?? 0) - (b.assignedPosition ?? 0));
  const appliedUnassigned = participations.filter(
    (p) => (slot === 'A' ? p.appliedA : p.appliedB) && p.assignedSlot !== slot,
  );

  return (
    <div className="rounded border border-slate-800 bg-slate-950/40 p-3">
      <div className="mb-2 flex items-baseline justify-between">
        <div className="text-sm font-semibold">
          Slot {slot}{' '}
          <span className="ml-1 text-xs font-normal text-slate-400">
            @ {slotInfo?.startsAtServer}
          </span>
        </div>
        <span className="text-[10px] tabular-nums uppercase tracking-wider text-slate-500">
          {starters.length}/20 · {subs.length}/10
        </span>
      </div>

      {loading && <div className="text-xs text-slate-500">Loading…</div>}

      <Group label="Starters" rows={starters} />
      <Group label="Subs" rows={subs} />
      {appliedUnassigned.length > 0 && (
        <details className="mt-2 text-xs">
          <summary className="cursor-pointer text-slate-500 hover:text-slate-300">
            {appliedUnassigned.length} applied but not picked
          </summary>
          <ul className="mt-1 space-y-0.5 text-slate-400">
            {appliedUnassigned.map((p) => (
              <li key={p.id}>· {p.player.name}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function Group({ label, rows }: { label: string; rows: Participation[] }) {
  if (rows.length === 0)
    return (
      <div className="mb-2">
        <div className="flex items-baseline justify-between">
          <span className="text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
        </div>
        <div className="text-xs text-slate-600">none</div>
      </div>
    );
  const sumM1 = rows.reduce(
    (acc, r) => acc + (r.player.latestSnapshot?.m1Power ?? 0),
    0,
  );
  return (
    <div className="mb-2">
      <div className="flex items-baseline justify-between">
        <span className="text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
        <span className="text-[10px] tabular-nums text-slate-500" title="Sum of M1 Power">
          Σ {formatPower(sumM1)}
        </span>
      </div>
      <ul className="space-y-0.5">
        {rows.map((p, i) => (
          <li key={p.id} className="flex items-center gap-1.5 text-xs">
            <span className="w-5 tabular-nums text-slate-500">{i + 1}</span>
            <PlayerLabel player={p.player} className="flex-1 min-w-0" />
            <span className="tabular-nums text-slate-400" title="M1 Power">
              {formatPower(p.player.latestSnapshot?.m1Power)}
            </span>
            <AttendanceIcon status={p.attendanceStatus} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function AttendanceIcon({ status }: { status: AttendanceStatus | null }) {
  if (status === 'showed')
    return <Check size={12} className="text-emerald-400" aria-label="Attended" />;
  if (status === 'late')
    return <Clock size={12} className="text-yellow-400" aria-label="Late" />;
  if (status === 'no_show')
    return <X size={12} className="text-red-400" aria-label="No-show" />;
  return (
    <CircleDashed size={12} className="text-slate-600" aria-label="Attendance not marked" />
  );
}
